import React from 'react';
import { ExternalLink } from 'lucide-react';
import { DealDetail, DealProduct, DealStage } from '../../../types';
import { CustomerContactListItem } from '../../../types/customer-contact';
import { CustomerContact } from '../../../types/customer-contact';
import { DEAL_STAGES, calculateProducts } from '../utils/dealStages';
import { LightboxType, renderUrlPreview } from '../../../utils/filePreview';

interface DealViewPanelProps {
  selectedDeal: DealDetail;
  panelError: string | null;
  submitting: boolean;
  customerContacts: CustomerContactListItem[];
  loadingContacts: boolean;
  handleStageTransition: (stage: DealStage) => void;
  openLightbox: (url: string, type?: LightboxType) => void;
}

const formatContactAddress = (contact: CustomerContactListItem | CustomerContact): string => {
  const parts = [
    (contact as any).street,
    (contact as any).city,
    (contact as any).state,
    (contact as any).zip,
    (contact as any).country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '-';
};

const DealViewPanel: React.FC<DealViewPanelProps> = ({
  selectedDeal,
  panelError,
  submitting,
  customerContacts,
  loadingContacts,
  handleStageTransition,
  openLightbox,
}) => {
  return (
    <div className="space-y-4">
      {panelError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {panelError}
        </div>
      )}
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Section - Deals Details and Customer */}
        <div className="space-y-4">
          {/* Deals Details */}
          <div className="space-y-3 theme-card rounded-lg p-3 theme-border">
            <h3 className="text-base font-semibold theme-text-primary border-b theme-border pb-1.5">Deals Details</h3>

            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                  Deal Name
                </label>
                <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                  <p className="text-sm theme-text-primary">{selectedDeal.name}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                  Type
                </label>
                <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                  <p className="text-sm theme-text-primary">{selectedDeal.type || '-'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                  Stage
                </label>
                <select
                  value={selectedDeal.stage}
                  onChange={(e) => handleStageTransition(e.target.value as DealStage)}
                  disabled={submitting}
                  className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-secondary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {DEAL_STAGES.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                  Project
                </label>
                <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                  <p className="text-sm theme-text-primary">
                    {selectedDeal.project ? selectedDeal.project.project_number : '-'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                  Project Request ID
                </label>
                <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                  <p className="text-sm theme-text-primary">
                    {selectedDeal.project_request_id || '-'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                  Created At
                </label>
                <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                  <p className="text-sm theme-text-primary">
                    {new Date(selectedDeal.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                  Updated At
                </label>
                <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                  <p className="text-sm theme-text-primary">
                    {new Date(selectedDeal.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Notes in Deals Details */}
            <div>
              <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                Notes
              </label>
              <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary min-h-[80px]">
                <p className="text-sm theme-text-primary whitespace-pre-wrap">
                  {selectedDeal.notes || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Data */}
          {selectedDeal.customer && (
            <div className="space-y-3 theme-card rounded-lg p-3 theme-border">
              <h3 className="text-base font-semibold theme-text-primary border-b theme-border pb-1.5">Customer</h3>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                    Customer Name
                  </label>
                  <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                    <p className="text-sm theme-text-primary">{selectedDeal.customer.item || '-'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                    Client ID
                  </label>
                  <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                    <p className="text-sm theme-text-primary">{selectedDeal.customer.client_id || '-'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                    Short Name
                  </label>
                  <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                    <p className="text-sm theme-text-primary">{selectedDeal.customer.short_name || '-'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                    Status
                  </label>
                  <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                    <p className="text-sm theme-text-primary capitalize">{selectedDeal.customer.status || '-'}</p>
                  </div>
                </div>
              </div>
              {selectedDeal.customer.client_drive_name && (
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                    Client Drive Name
                  </label>
                  <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                    <p className="text-sm theme-text-primary">{selectedDeal.customer.client_drive_name}</p>
                  </div>
                </div>
              )}
              {selectedDeal.customer.google_drive_url && (
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                    Google Drive URL
                  </label>
                  <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                    <a
                      href={selectedDeal.customer.google_drive_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline text-sm"
                    >
                      {selectedDeal.customer.google_drive_url}
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Customer Contacts Section */}
          {selectedDeal.customer && (
            <div className="space-y-3 theme-card rounded-lg p-3 theme-border">
              <h3 className="text-base font-semibold theme-text-primary border-b theme-border pb-1.5">Customer Contacts</h3>
              {loadingContacts ? (
                <div className="text-center py-4 text-sm theme-text-tertiary">Loading contacts...</div>
              ) : customerContacts.length === 0 ? (
                <div className="text-center py-4 text-sm theme-text-tertiary">No contacts available</div>
              ) : (
                <div className="space-y-2">
                  {customerContacts.map((contact) => (
                    <div key={contact.id} className="theme-border rounded px-2 py-1.5 theme-bg-secondary">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <p className="text-sm font-medium theme-text-primary">{contact.name}</p>
                            {contact.is_primary && (
                              <span className="px-1.5 py-0.5 text-sm bg-blue-100 text-blue-800 rounded">Primary</span>
                            )}
                          </div>
                          {contact.title && (
                            <p className="text-sm theme-text-secondary mb-0.5">{contact.title}</p>
                          )}
                          {contact.email && (
                            <p className="text-sm theme-text-secondary mb-0.5">
                              <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                                {contact.email}
                              </a>
                            </p>
                          )}
                          {contact.phone && (
                            <p className="text-sm theme-text-secondary mb-0.5">
                              <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-800">
                                {contact.phone}
                              </a>
                            </p>
                          )}
                          {formatContactAddress(contact) !== '-' && (
                            <p className="text-sm theme-text-secondary">{formatContactAddress(contact)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Section - Intake Form Work Request Details and Products Details */}
        <div className="space-y-4">
          {/* Intake Form Work Request Details */}
          {(() => {
            const projectRequest = selectedDeal.projectRequest || (selectedDeal as any).project_request;
            return projectRequest ? (
              <div className="space-y-3 theme-card rounded-lg p-3 theme-border">
                <h3 className="text-base font-semibold theme-text-primary border-b theme-border pb-1.5">Intake Form Work Request Details</h3>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                      Work Request
                    </label>
                    <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                      {projectRequest.work_request && projectRequest.work_request.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {projectRequest.work_request.map((req: string, index: number) => (
                            <span
                              key={index}
                              className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-sm"
                            >
                              {req}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm theme-text-tertiary">-</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                      Impacted Parcels
                    </label>
                    <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                      <p className="text-sm theme-text-primary">
                        {projectRequest.impacted_parcels ?? '-'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                      Research - Government Owned
                    </label>
                    <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                      <p className="text-sm theme-text-primary">
                        {projectRequest.research_is_government_owned === true ? 'Yes' :
                          projectRequest.research_is_government_owned === false ? 'No' : '-'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                      Acquisition - Government Owned
                    </label>
                    <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                      <p className="text-sm theme-text-primary">
                        {projectRequest.acquisition_is_government_owned === true ? 'Yes' :
                          projectRequest.acquisition_is_government_owned === false ? 'No' : '-'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium theme-text-secondary mb-0.5">
                      QA/QC - Has Documentation
                    </label>
                    <div className="w-full theme-border rounded px-2 py-1 theme-bg-secondary">
                      <p className="text-sm theme-text-primary">
                        {projectRequest.qa_has_documentation === true ? 'Yes' :
                          projectRequest.qa_has_documentation === false ? 'No' : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 theme-card rounded-lg p-3 theme-border">
                <h3 className="text-base font-semibold theme-text-primary border-b theme-border pb-1.5">Intake Form Work Request Details</h3>
                <div className="text-sm theme-text-tertiary p-3 theme-border rounded theme-bg-secondary">
                  No project request data available.
                </div>
              </div>
            );
          })()}

          {/* Products Details */}
          <div className="space-y-3 theme-card rounded-lg p-3 theme-border">
            <h3 className="text-base font-semibold theme-text-primary border-b theme-border pb-1.5">Products Details</h3>
            {(() => {
              // Check if stage is Value Proposition or later
              const shouldShowProducts = selectedDeal.stage === 'Value Proposition' ||
                DEAL_STAGES.indexOf(selectedDeal.stage) > DEAL_STAGES.indexOf('Value Proposition');

              if (!shouldShowProducts) {
                return (
                  <div className="text-sm theme-text-tertiary p-3 theme-border rounded theme-bg-secondary">
                    Products will be calculated when stage is set to "Value Proposition".
                  </div>
                );
              }

              // Handle both camelCase and snake_case (Laravel returns snake_case)
              const projectRequest = selectedDeal.projectRequest || (selectedDeal as any).project_request;
              const hasProjectRequest = !!projectRequest;
              const hasCustomer = !!selectedDeal.customer;

              if (!hasProjectRequest || !hasCustomer) {
                const missingItems: string[] = [];
                if (!hasProjectRequest) missingItems.push('Project Request');
                if (!hasCustomer) missingItems.push('Customer');
                return (
                  <div className="text-sm theme-text-tertiary p-3 theme-border rounded theme-bg-secondary">
                    Unable to calculate products: Missing {missingItems.join(' and ')} data.
                    {selectedDeal.project_request_id && !hasProjectRequest && (
                      <div className="mt-1.5 text-sm theme-text-tertiary">
                        Note: Project Request ID {selectedDeal.project_request_id} exists but the relationship could not be loaded.
                      </div>
                    )}
                    {selectedDeal.customer_id && !hasCustomer && (
                      <div className="mt-1.5 text-sm theme-text-tertiary">
                        Note: Customer ID {selectedDeal.customer_id} exists but the relationship could not be loaded.
                      </div>
                    )}
                  </div>
                );
              }

              // Get products from API (dealProducts or deal_products) or calculate as fallback
              const dbProducts = selectedDeal.dealProducts || selectedDeal.deal_products || [];

              let productsToDisplay: DealProduct[] = [];

              if (dbProducts.length > 0) {
                // Use products from database
                productsToDisplay = dbProducts.map(p => ({
                  itemName: p.product_name,
                  unit: p.unit_price,
                  quantity: p.quantity,
                  total: p.total_price,
                }));
              } else {
                // Fallback to calculated products if no DB products
                productsToDisplay = calculateProducts(selectedDeal);
              }

              return productsToDisplay.length > 0 ? (
                <div className="theme-border rounded overflow-hidden">
                  <table className="min-w-full divide-y theme-border">
                    <thead className="theme-table-header">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-sm font-medium theme-text-primary uppercase tracking-wider">
                          Item Name
                        </th>
                        <th className="px-2 py-1.5 text-left text-sm font-medium theme-text-primary uppercase tracking-wider">
                          Unit
                        </th>
                        <th className="px-2 py-1.5 text-left text-sm font-medium theme-text-primary uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-2 py-1.5 text-left text-sm font-medium theme-text-primary uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="theme-bg-primary divide-y theme-border">
                      {productsToDisplay.map((product, index) => (
                        <tr key={index} className="theme-table-row">
                          <td className="px-2 py-1.5 text-sm theme-text-primary">{product.itemName}</td>
                          <td className="px-2 py-1.5 text-sm theme-text-secondary">{product.unit}</td>
                          <td className="px-2 py-1.5 text-sm theme-text-secondary">{product.quantity}</td>
                          <td className="px-2 py-1.5 text-sm theme-text-primary font-medium">{product.total}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="theme-table-header theme-border-t">
                      <tr>
                        <td colSpan={3} className="px-2 py-1.5 text-sm font-semibold theme-text-primary text-right">
                          Total:
                        </td>
                        <td className="px-2 py-1.5 text-sm font-semibold theme-text-primary">
                          {productsToDisplay.reduce((sum, product) => sum + product.total, 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-sm theme-text-tertiary p-3 theme-border rounded theme-bg-secondary">
                  No products available. Products will be calculated when stage is set to "Value Proposition".
                </div>
              );
            })()}
          </div>

          {/* Intake Form Documents */}
          {(() => {
            const projectRequest = selectedDeal.projectRequest || (selectedDeal as any).project_request;
            const hasRequestEmailFile = projectRequest?.request_email_file_url;
            const hasSupportingDocs = projectRequest?.supporting_docs_url;

            if (!hasRequestEmailFile && !hasSupportingDocs) {
              return null;
            }

            return (
              <div className="space-y-3 theme-card rounded-lg p-3 theme-border">
                <h3 className="text-base font-semibold theme-text-primary border-b theme-border pb-1.5">Intake Form Documents</h3>
                <div className="space-y-4">
                  {hasRequestEmailFile && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium theme-text-primary">Request Email File</span>
                        <a
                          href={projectRequest.request_email_file_url || ''}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open in new tab
                        </a>
                      </div>
                      {renderUrlPreview(
                        projectRequest.request_email_file_url || '',
                        projectRequest.request_email_file_name || '',
                        'Request Email File',
                        openLightbox,
                      )}
                    </div>
                  )}
                  {hasSupportingDocs && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium theme-text-primary">Supporting Documents</span>
                        <a
                          href={projectRequest.supporting_docs_url || ''}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open in new tab
                        </a>
                      </div>
                      {renderUrlPreview(
                        projectRequest.supporting_docs_url || '',
                        projectRequest.supporting_docs_file_name || '',
                        'Supporting Documents',
                        openLightbox,
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default DealViewPanel;
